import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storage, db, auth } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, getStorage, uploadBytes } from 'firebase/storage';
import { doc, updateDoc, getDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getIdToken } from 'firebase/auth';
import { CheckCircleIcon, EnvelopeIcon, PhoneIcon, ClockIcon, ExclamationCircleIcon, ArchiveBoxIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { Disclosure, Menu } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import profileImage from '../images/images.png';
import logo from '../images/logo.png';
import { LoanStatuses } from '../constants/statuses';
import { EllipsisHorizontalIcon, DocumentIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { CheckIcon } from '@heroicons/react/20/solid';

const user = {
  name: 'Tom Cook',
  email: 'tom@example.com',
  imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
};

const navigation = [
  { name: 'Dashboard', href: '#', current: true },
  { name: 'Team', href: '#', current: false },
  { name: 'Projects', href: '#', current: false },
  { name: 'Calendar', href: '#', current: false },
  { name: 'Reports', href: '#', current: false },
];

const userNavigation = [
  { name: 'Your Profile', href: '#' },
  { name: 'Settings', href: '#' },
  { name: 'Sign out', href: '#' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const requiredDocuments = [
  {
    DocumentID: 6,
    Name: "Business Tax Returns",
    Description: "Upload the last 3 years of business tax returns.",
    RequiredDocuments: 3,
    Individual: false,
    Labels: [
      "Business Tax Return - Current Year",
      "Business Tax Return - Last Year",
      "Business Tax Return - 2 Years Ago"
    ],
    loanPurpose: ["Acquisition", "workingCapital"]
  },
  {
    DocumentID: 10,
    Name: "Interim P&L",
    Description: "Upload the interim Profit and Loss statement (PDF or Excel).",
    RequiredDocuments: 1,
    Individual: false,
    Labels: ["Interim P&L"],
    AcceptedFileTypes: ".pdf,.xlsx,.xls",
    loanPurpose: ["Acquisition", "workingCapital"]
  },
  {
    DocumentID: 19,
    Name: "Balance Sheet",
    Description: "Upload your 2024 balance sheet (PDF or Excel).",
    RequiredDocuments: 1,
    Individual: false,
    Labels: ["Balance Sheet 2024"],
    AcceptedFileTypes: ".pdf,.xlsx,.xls",
    loanPurpose: ["Acquisition", "workingCapital"]
  },
  {
    DocumentID: 20,
    Name: "Personal Financial Statement",
    Description: "Upload Personal Financial Statement (SBA Form 413) for each guarantor.",
    RequiredDocuments: 1,
    Individual: true,
    Labels: ["SBA Form 413"],
    FormLink: "https://www.sba.gov/sites/default/files/2022-08/SBA%20Form%20413%20%287a-504-SBG-WOSB-8a%29_8.9.2022-508.pdf",
    TemplateLink: "/templates/form413.pdf",
    loanPurpose: ["Acquisition", "workingCapital", "workingCapital-startup"]
  },
  {
    DocumentID: 4,
    Name: "Personal Tax Returns",
    Description: "Upload the last 3 years of personal tax returns for each guarantor.",
    RequiredDocuments: 3,
    Individual: true,
    Labels: [
      "Personal Tax Return - Current Year",
      "Personal Tax Return - Last Year",
      "Personal Tax Return - 2 Years Ago"
    ],
    loanPurpose: ["Acquisition", "workingCapital", "workingCapital-startup"]
  },
  {
    DocumentID: 15,
    Name: "Bank Statements",
    Description: "Upload 3 months of bank statements.",
    RequiredDocuments: 1,
    Individual: false,
    Labels: ["Bank Statement - Most Recent","Bank Statement - 2 Months Ago", "Bank Statement - 3 Months Ago"],
    AcceptedFileTypes: ".pdf,.xlsx,.xls",
    loanPurpose: ["workingCapital-nonsba", "workingCapital"]
  },
  {
    DocumentID: 16,
    Name: "Debt Schedule",
    Description: "Upload your company's debt schedule. Download our template for the correct format.",
    RequiredDocuments: 1,
    Individual: false,
    Labels: ["Debt Schedule"],
    AcceptedFileTypes: ".pdf,.xlsx,.xls",
    TemplateLink: "/templates/debtschedule.pdf",
    loanPurpose: ["Acquisition", "workingCapital", "workingCapital-startup"]
  },
  {
    DocumentID: 21,
    Name: "AR/AP Aging Summary (If Applicable)",
    Description: "Upload your accounts receivable and accounts payable aging summary.",
    SubDescription: "Only required if your business has accounts receivable or accounts payable. This helps us understand your business's cash flow cycle and outstanding payments.",
    RequiredDocuments: 1,
    Individual: false,
    Labels: ["AR/AP Aging Summary"],
    AcceptedFileTypes: ".pdf,.xlsx,.xls",
    loanPurpose: ["Acquisition", "workingCapital"]
  },
  {
    DocumentID: 22,
    Name: "Purchase Agreement (If Applicable)",
    Description: "Upload draft of purchase agreement.",
    SubDescription: "Only required if you are purchasing real estate or an existing business. This document should outline the terms and conditions of the proposed purchase.",
    RequiredDocuments: 1,
    Individual: false,
    Labels: ["Purchase Agreement"],
    AcceptedFileTypes: ".pdf,.doc,.docx",
    loanPurpose: ["Acquisition"]
  }
];

const tabs = [
  { name: 'Questions', current: true },
  { name: 'Documents', current: false, locked: true }
]

const SharedUploadPage = () => {
  const { loanId } = useParams();
  const [uploading, setUploading] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [numberOfOwners, setNumberOfOwners] = useState('');
  const [personalGuarantors, setPersonalGuarantors] = useState([]);
  const [creditScores, setCreditScores] = useState([]);
  const [businessName, setBusinessName] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('0');
  const [workingCapitalUse, setWorkingCapitalUse] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [step, setStep] = useState(0);
  const [numberOfEmployees, setNumberOfEmployees] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [processingStatus, setProcessingStatus] = useState({});
  const [currentTab, setCurrentTab] = useState('Questions');
  const [currentTaxReturn, setCurrentTaxReturn] = useState(0);
  const navigate = useNavigate();
  const [showUploadInterface, setShowUploadInterface] = useState(false);
  const [prequalifyAmount, setPrequalifyAmount] = useState(null);
  const [termSheetAmount, setTermSheetAmount] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loanStatus, setLoanStatus] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loan, setLoan] = useState(null);
  const [requestedDocsList, setRequestedDocsList] = useState([]);
  const [loanDataForNotifications, setLoanDataForNotifications] = useState(null);

  const people = [
    {
      name: 'Tom Cook',
      title: 'Loan Officer',
      role: 'Member',
      email: 'tom@example.com',
      telephone: '+1 (555) 123-4567',
      imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    },
  ];

  const getStatusStyle = (isUploaded) => {
    if (isUploaded) {
      return 'text-green-700 bg-green-50 ring-green-600/20';
    }
    return 'text-gray-600 bg-gray-50 ring-gray-500/10';
  };

  useEffect(() => {
    const fetchLoanApplicationData = async () => {
      if (loanId) {
        setIsLoading(true);
        try {
          const loanDoc = await getDoc(doc(db, 'loanApplications', loanId));
          if (loanDoc.exists()) {
            const loanData = loanDoc.data();
            setLoan({ id: loanDoc.id, ...loanData });
            
            // Fetch uploaded documents for this specific loan application
            const uploadedDocsQuery = query(
              collection(db, 'documentMetadata'),
              where('loanApplicationId', '==', loanId)
            );
            const uploadedDocsSnapshot = await getDocs(uploadedDocsQuery);
            const uploadedDocsData = {};
            uploadedDocsSnapshot.forEach((doc) => {
              const data = doc.data();
              const key = data.requestedDocumentName || `${data.documentName}-${data.label}${data.guarantorId ? `-${data.guarantorId}` : ''}`;
              uploadedDocsData[key] = {
                url: data.fileUrl,
                name: data.fileName,
                uploadedAt: data.uploadedAt,
                metadataId: doc.id,
                isRequest: !!data.requestedDocumentName
              };
            });

            setUploadedFiles(uploadedDocsData);
            setBusinessName(loanData.businessName || '');
            setYearsInBusiness(loanData.yearsInBusiness !== undefined ? String(loanData.yearsInBusiness) : '0');
            setWorkingCapitalUse(loanData.workingCapitalUse || '');
            setLoanAmount(loanData.loanSize !== undefined ? String(loanData.loanSize) : '');
            setPersonalGuarantors(loanData.personalGuarantors || []);
            setCreditScores(loanData.creditScores ? loanData.creditScores.map(String) : []);
            setNumberOfEmployees(loanData.numberOfEmployees !== undefined ? String(loanData.numberOfEmployees) : '');
            setEmails(loanData.emails || []);
            setLoanStatus(loanData.loanStatus || '');
            setPrequalifyAmount(loanData.prequalifyAmount || null);
            setTermSheetAmount(loanData.termSheetAmount || null);
            setRequestedDocsList(loanData.requestedDocuments || []);
            setLoanDataForNotifications(loanData);

            if (loanData.businessName && loanData.yearsInBusiness !== undefined &&
                loanData.workingCapitalUse && loanData.loanSize) {
              setShowDocumentUpload(true);
            }
          }
        } catch (error) {
          console.error("Error fetching loan application data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchLoanApplicationData();
  }, [loanId]);

  useEffect(() => {
    const fetchPrequalifyAmount = async () => {
      if (loanId) {
        const loanRef = doc(db, 'loanApplications', loanId);
        const loanSnap = await getDoc(loanRef);
        if (loanSnap.exists()) {
          setPrequalifyAmount(loanSnap.data().prequalifyAmount);
        }
      }
    };
    
    fetchPrequalifyAmount();
  }, [loanId]);

  const renderForm = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="numberOfOwners" className="block text-sm font-medium text-gray-700">
                How many owners have more than 20% ownership?
              </label>
              <input
                type="number"
                id="numberOfOwners"
                value={numberOfOwners}
                onChange={(e) => {
                  const value = e.target.value;
                  const num = parseInt(value) || 0;
                  if (num >= 0 && num <= 100) {
                    setNumberOfOwners(value);
                    setPersonalGuarantors(num ? Array(num).fill().map(() => ({ firstName: '', lastName: '' })) : []);
                    setCreditScores(num ? Array(num).fill('') : []);
                    setEmails(num ? Array(num).fill('') : []);
                  }
                }}
                required
                min="0"
                max="100"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6">
            {personalGuarantors.map((guarantor, index) => (
              <div key={index} className="border-b pb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Guarantor {index + 1}</h3>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-3">
                    <label htmlFor={`firstName-${index}`} className="block text-sm font-medium text-gray-700">
                      First name
                    </label>
                    <input
                      type="text"
                      id={`firstName-${index}`}
                      value={guarantor.firstName}
                      onChange={(e) => {
                        const newGuarantors = [...personalGuarantors];
                        newGuarantors[index].firstName = e.target.value;
                        setPersonalGuarantors(newGuarantors);
                      }}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label htmlFor={`lastName-${index}`} className="block text-sm font-medium text-gray-700">
                      Last name
                    </label>
                    <input
                      type="text"
                      id={`lastName-${index}`}
                      value={guarantor.lastName}
                      onChange={(e) => {
                        const newGuarantors = [...personalGuarantors];
                        newGuarantors[index].lastName = e.target.value;
                        setPersonalGuarantors(newGuarantors);
                      }}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="sm:col-span-6">
                    <label htmlFor={`email-${index}`} className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      id={`email-${index}`}
                      value={emails[index]}
                      onChange={(e) => {
                        const newEmails = [...emails];
                        newEmails[index] = e.target.value;
                        setEmails(newEmails);
                      }}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="sm:col-span-6">
                    <label htmlFor={`creditScore-${index}`} className="block text-sm font-medium text-gray-700">
                      Credit Score
                    </label>
                    <input
                      type="number"
                      id={`creditScore-${index}`}
                      value={creditScores[index]}
                      onChange={(e) => {
                        const newCreditScores = [...creditScores];
                        newCreditScores[index] = e.target.value;
                        setCreditScores(newCreditScores);
                      }}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">Business Name</label>
              <input
                type="text"
                id="businessName"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-gray-700">
                How many years have you been in business?
              </label>
              <input
                type="number"
                id="yearsInBusiness"
                value={yearsInBusiness}
                onChange={(e) => setYearsInBusiness(e.target.value)}
                required
                min="0"
                max="100"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="workingCapitalUse" className="block text-sm font-medium text-gray-700">Working Capital Use</label>
              <textarea
                id="workingCapitalUse"
                value={workingCapitalUse}
                onChange={(e) => setWorkingCapitalUse(e.target.value)}
                required
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700">
                Loan Amount (Min: $50,000 - Max: $5,000,000)
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="text"
                  id="loanAmount"
                  value={loanAmount}
                  onChange={(e) => {
                    // Remove any non-numeric characters except decimal point
                    const value = e.target.value.replace(/[^\d.]/g, '');
                    setLoanAmount(value);
                  }}
                  onBlur={(e) => {
                    // Validate on blur
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 50000) {
                      setLoanAmount('50000');
                    } else if (value > 5000000) {
                      setLoanAmount('5000000');
                    }
                  }}
                  className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="50,000"
                />
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="numberOfEmployees" className="block text-sm font-medium text-gray-700">Number of Employees</label>
              <input
                type="number"
                id="numberOfEmployees"
                value={numberOfEmployees}
                onChange={(e) => setNumberOfEmployees(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loanRef = doc(db, 'loanApplications', loanId);
    await updateDoc(loanRef, {
      firstName: personalGuarantors[0]?.firstName || '',
      lastName: personalGuarantors[0]?.lastName || '',
      personalGuarantors,
      creditScores,
      emails,
      businessName,
      yearsInBusiness,
      workingCapitalUse,
      loanSize: loanAmount,
      numberOfEmployees,
      lastUserUpdate: new Date(),
      loanStatus: LoanStatuses.NEW_APPLICATION,
      loanStructure: {
        sba7aLoan: "100",
        additionalNotes: ""
      }
    });
    setShowDocumentUpload(true);
  };

  const sendRequestedDocsCompleteNotification = async (loanData) => {
    if (!loanData || !loanData.id) {
        console.error("Missing loan data for internal notification");
        return false;
    }
    const applicantName = `${loanData.firstName || ''} ${loanData.lastName || ''}`.trim() || 'Applicant';
    const business = loanData.businessName || 'their business';
    const adminLoanLink = `https://lended.ai/loan-details/${loanData.id}`; // Adjust URL as needed

    try {
        const emailRef = collection(db, "email");
        const emailData = {
            to: "max@sbacapital.ai", // Hardcoded recipient
            from: {
                email: "isak@lended.ai", // Sender address
                name: "Isak @ Lended"
            },
            replyTo: "isak@lended.ai",
            subject: `Requested Docs Uploaded: ${applicantName} - ${business}`,
            templateName: "InternalNotification", // Or a specific template
            message: {
                subject: `Requested Docs Uploaded: ${applicantName} - ${business}`,
                // Simple text or HTML body
                text: `All requested documents have been uploaded by ${applicantName} for ${business}. Please review the application: ${adminLoanLink}`,
                html: `<p>All requested documents have been uploaded by <strong>${applicantName}</strong> for <strong>${business}</strong>.</p>
                       <p>Please review the application in the admin portal:</p>
                       <p><a href="${adminLoanLink}">View Loan Details</a></p>`
            }
        };
        await addDoc(emailRef, emailData);
        console.log("Internal notification email sent for completed requested docs.");
        return true;
    } catch (error) {
        console.error("Error sending internal notification email:", error);
        return false;
    }
  };

  const sendClosingDocsCompleteNotification = async (loanData) => {
    if (!loanData || !loanData.id) {
        console.error("Missing loan data for internal closing notification");
        return false;
    }
    const applicantName = `${loanData.firstName || ''} ${loanData.lastName || ''}`.trim() || 'Applicant';
    const business = loanData.businessName || 'their business';
    const adminLoanLink = `https://lended.ai/loan-details/${loanData.id}`; // Adjust URL as needed

    try {
        const emailRef = collection(db, "email");
        const emailData = {
            to: "max@sbacapital.ai", // Hardcoded recipient
            from: {
                email: "isak@lended.ai", // Sender address
                name: "Isak @ Lended"
            },
            replyTo: "isak@lended.ai",
            subject: `Closing Docs Uploaded: ${applicantName} - ${business}`,
            templateName: "InternalClosingDocsNotification", // Specific template
            message: {
                subject: `Closing Docs Uploaded: ${applicantName} - ${business}`,
                text: `All closing documents have been uploaded by ${applicantName} for ${business}. Please review the application to finalize funding: ${adminLoanLink}`,
                html: `<p>All closing documents have been uploaded by <strong>${applicantName}</strong> for <strong>${business}</strong>.</p>
                       <p>Please review the application in the admin portal to finalize funding:</p>
                       <p><a href="${adminLoanLink}">View Loan Details</a></p>`
            }
        };
        await addDoc(emailRef, emailData);
        console.log("Internal notification email sent for completed closing docs.");
        return true;
    } catch (error) {
        console.error("Error sending internal closing notification email:", error);
        return false;
    }
  };

  // --- Add a new notification function (example) ---
  const sendApplicationSubmittedNotification = async (loanData) => {
      if (!loanData || !loanData.id) {
          console.error("Missing loan data for application submitted notification");
          return false;
      }
      const applicantName = `${loanData.firstName || ''} ${loanData.lastName || ''}`.trim() || 'Applicant';
      const business = loanData.businessName || 'their business';
      const adminLoanLink = `https://lended.ai/loan-details/${loanData.id}`; // Adjust URL as needed

      try {
          const emailRef = collection(db, "email");
          const emailData = {
              to: "max@sbacapital.ai", // Hardcoded recipient
              from: {
                  email: "isak@lended.ai", // Sender address
                  name: "Isak @ Lended"
              },
              replyTo: "isak@lended.ai",
              subject: `Application Submitted: ${applicantName} - ${business}`,
              templateName: "InternalApplicationSubmitted", // Use a specific template
              message: {
                  subject: `Application Submitted: ${applicantName} - ${business}`,
                  text: `All required documents have been uploaded by ${applicantName} for ${business}. The application is ready for review: ${adminLoanLink}`,
                  html: `<p>All required documents have been uploaded by <strong>${applicantName}</strong> for <strong>${business}</strong>.</p>
                         <p>The application is now marked as submitted and ready for review:</p>
                         <p><a href="${adminLoanLink}">View Loan Details</a></p>`
              }
          };
          await addDoc(emailRef, emailData);
          console.log("Internal notification email sent for application submitted.");
          return true;
      } catch (error) {
          console.error("Error sending application submitted notification email:", error);
          return false;
      }
  };

  const handleFileUpload = async (e, docName, label, guarantor = null, requestedDocName = null) => {
    const file = e.target.files[0];
    if (file) {
      const guarantorId = guarantor ? `${guarantor.firstName} ${guarantor.lastName}` : null;
      // Ensure fileIdentifier is always the cleaned base document name for standard uploads
      const baseDocName = requestedDocName ? null : (docName ? docName.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim() : null);
      const fileIdentifier = requestedDocName || baseDocName; // Key for uploadedDocuments map uses base name
      const fileKey = requestedDocName || `${baseDocName}-${label}${guarantorId ? `-${guarantorId}` : ''}`; // Key for local UI state (uploadedFiles)

      setProcessingStatus(prevStatus => ({ ...prevStatus, [fileKey]: 'processing' }));

      let shouldSendRegularNotification = false;
      let shouldSendClosingNotification = false;
      let shouldSendSubmittedNotification = false; // Flag for new notification

      try {
        const storage = getStorage();
        const userId = auth.currentUser?.uid || 'anonymous';
        const pathSegment = requestedDocName ? `requested/${requestedDocName}` : `${fileIdentifier}/${label}${guarantorId ? `/${guarantorId}` : ''}`;
        const storageRef = ref(storage, `documents/${userId}/${loanId}/${pathSegment}/${file.name}`);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
  
        const fileMetadata = {
          documentName: fileIdentifier, // Use base name for standard docs metadata
          label: requestedDocName ? null : label,
          fileName: file.name,
          fileUrl: downloadURL,
          uploadedAt: new Date().toISOString(),
          loanApplicationId: loanId,
          userId: userId,
          businessName: businessName,
          uploadedBy: 'borrower',
          guarantorId: guarantorId,
          ...(requestedDocName && { requestedDocumentName: requestedDocName }) // Keep specific requested name if applicable
        };
  
        const metadataRef = collection(db, 'documentMetadata');
        const docRef = await addDoc(metadataRef, fileMetadata);
        
        setUploadedFiles(prevFiles => ({
          ...prevFiles,
          [fileKey]: { 
            name: file.name, 
            url: downloadURL,
            metadataId: docRef.id,
            isRequest: !!requestedDocName
          }
        }));
  
        const loanRef = doc(db, 'loanApplications', loanId);
        const updateData = { lastUpdated: new Date().toISOString() };
        let updatedRequestedDocs = [...requestedDocsList];
        let allRequestedNowUploaded = false;

        // --- Prepare Firestore updateData ---
        if (requestedDocName) {
            updatedRequestedDocs = requestedDocsList.map(reqDoc =>
                reqDoc.name === requestedDocName ? { ...reqDoc, status: 'uploaded', uploadedAt: new Date().toISOString() } : reqDoc
            );
            updateData.requestedDocuments = updatedRequestedDocs;
            allRequestedNowUploaded = updatedRequestedDocs.every(d => d.status === 'uploaded');

            if (allRequestedNowUploaded) {
                const currentLoanStatus = loan?.loanStatus; // Use local state
                const regularNotified = loan?.requestedDocsCompletedNotified;
                const closingNotified = loan?.closingDocsCompletedNotified;

                if (currentLoanStatus === LoanStatuses.DOCUMENT_CLARIFICATION && !regularNotified) {
                    shouldSendRegularNotification = true;
                    updateData.requestedDocsCompletedNotified = true;
                } else if (currentLoanStatus === LoanStatuses.CLOSING && !closingNotified) {
                    shouldSendClosingNotification = true;
                    updateData.closingDocsCompletedNotified = true;
                }
            }
        } else if (fileIdentifier) { // Standard document upload
             // Store the uploaded status keyed by the cleaned base document name
             const cleanedFileIdentifierKey = `uploadedDocuments.${fileIdentifier}`;
             updateData[cleanedFileIdentifierKey] = true; // Use dot notation for nested field update

            // --- Check if all *truly required* docs are now uploaded ---
            const optionalDocNames = [
                "AR/AP Aging Summary (If Applicable)", // Use original names for filtering
                "Purchase Agreement (If Applicable)"
            ];
            const trulyRequiredDocuments = requiredDocuments.filter(doc => !optionalDocNames.includes(doc.Name));

            // Check against the potential *next* state of uploadedDocuments in Firestore
            // Need to fetch current state and merge updateData to check accurately
            const currentLoanSnapshot = await getDoc(loanRef);
            const currentLoanData = currentLoanSnapshot.data();
            const potentiallyCompleteDocs = { ...(currentLoanData?.uploadedDocuments || {}), [fileIdentifier]: true }; // Simulate the update

            const areAllRequiredUploaded = trulyRequiredDocuments.every(doc => {
                const cleanedDocName = doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim();
                return !!potentiallyCompleteDocs[cleanedDocName];
            });

            // If all required docs are uploaded and status is New, update status
            // Use currentLoanData.loanStatus for the check before update
            if (areAllRequiredUploaded && currentLoanData?.loanStatus === LoanStatuses.NEW_APPLICATION) {
                updateData.loanStatus = LoanStatuses.SUBMITTED;
                shouldSendSubmittedNotification = true; // Set flag to send new notification
                console.log("All required documents uploaded, updating status to SUBMITTED.");
            }
        }

        // --- Update Firestore ---
        await updateDoc(loanRef, updateData);
  
        // --- Update local state immediately ---
        // Fetch the potentially updated loan data again *after* the update, or merge optimistically
        const currentLoanState = { ...loan }; // Capture current state before update
        const updatedLoanDataMerged = { ...currentLoanState, ...updateData }; // Merge update optimistically for notifications

        setLoan(prevLoan => {
            const newUploadedDocuments = { ...(prevLoan?.uploadedDocuments || {}) };
            if (!requestedDocName && fileIdentifier) {
                 // Update the nested structure correctly
                 newUploadedDocuments[fileIdentifier] = true;
            }

            const updatedLoan = {
                ...prevLoan,
                lastUpdated: updateData.lastUpdated,
                // Make sure uploadedDocuments is updated correctly
                uploadedDocuments: newUploadedDocuments,
                ...(updateData.requestedDocuments && { requestedDocuments: updatedRequestedDocs }),
                ...(updateData.requestedDocsCompletedNotified && { requestedDocsCompletedNotified: true }),
                ...(updateData.closingDocsCompletedNotified && { closingDocsCompletedNotified: true }),
                // Update status locally if it was changed in updateData
                ...(updateData.loanStatus && { loanStatus: updateData.loanStatus })
            };
            setLoanDataForNotifications(updatedLoan); // Use updated data for potential notifications

            // Update the separate loanStatus state variable if it was changed
            if (updateData.loanStatus) {
                 setLoanStatus(updateData.loanStatus); // <-- Ensure this updates the status used by render functions
                 console.log("Local loanStatus state updated to:", updateData.loanStatus); // Debug log
            }
            return updatedLoan;
        });

        // --- Send Notifications (check flags set earlier) ---
        if (shouldSendRegularNotification) {
             await sendRequestedDocsCompleteNotification(updatedLoanDataMerged); // Use merged data
        }
        if (shouldSendClosingNotification) {
             await sendClosingDocsCompleteNotification(updatedLoanDataMerged); // Use merged data
        }
        if (shouldSendSubmittedNotification) {
             await sendApplicationSubmittedNotification(updatedLoanDataMerged); // Use merged data
        }

        // --- Update other state ---
        if(requestedDocName) {
            setRequestedDocsList(updatedRequestedDocs);
        }
        
        setProcessingStatus(prevStatus => ({ ...prevStatus, [fileKey]: 'complete' }));
        setSuccessMessage(`${file.name} uploaded successfully`);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 3000);
  
        console.log('File uploaded successfully and local state updated');
      } catch (error) {
        console.error("Error uploading file:", error);
        setProcessingStatus(prevStatus => ({ ...prevStatus, [fileKey]: 'error' }));
        alert('Error uploading file. Please try again.');
      }
    }
  };

  const renderDocumentCard = (doc, label, documentKey, guarantor = null) => {
    const cleanDocName = doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim();
    const cleanDocumentKey = guarantor
        ? `${cleanDocName}-${label}-${guarantor.firstName} ${guarantor.lastName}`
        : `${cleanDocName}-${label}`;

    const isUploaded = uploadedFiles[cleanDocumentKey];
    const isMultipleDoc = doc.Labels.length > 1;
    
    const renderUploadStatus = () => (
      <div className="flex items-center gap-2">
        {isUploaded ? (
          <>
            <DocumentIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium text-green-600">
              {uploadedFiles[cleanDocumentKey].name}
            </span>
          </>
        ) : (
          <>
            <DocumentIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-500">
              No file uploaded
            </span>
          </>
        )}
      </div>
    );

    const renderTemplateDownload = () => {
      if (doc.TemplateLink) {
        return (
          <div className="mt-2">
            <a
              href={doc.TemplateLink}
              download
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Template
            </a>
          </div>
        );
      }
      return null;
    };

    const renderDottedUploadBox = () => (
      <div 
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, cleanDocName, label)}
        className={classNames(
          'border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center w-full',
          isUploaded ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400'
        )}
      >
        <input
          type="file"
          id={cleanDocumentKey.replace(/\s+/g, '-')}
          onChange={(e) => handleFileUpload(e, cleanDocName, label, guarantor, null)}
          className="sr-only"
          accept={doc.AcceptedFileTypes}
        />
        <label
          htmlFor={cleanDocumentKey.replace(/\s+/g, '-')}
          className="cursor-pointer text-center w-full"
        >
          {isUploaded ? (
            <>
              <DocumentIcon className="mx-auto h-12 w-12 text-green-500" />
              <span className="mt-2 block text-sm font-medium text-green-600">
                {uploadedFiles[cleanDocumentKey].name}
              </span>
              <span className="mt-1 block text-sm text-green-500">
                Click to replace
              </span>
            </>
          ) : (
            <>
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
              <span className="mt-2 block text-sm font-medium text-gray-900">
                Drop your file here, or click to upload
              </span>
              <span className="mt-1 block text-sm text-gray-500">
                {doc.AcceptedFileTypes ? `Accepted formats: ${doc.AcceptedFileTypes.replace(/\./g, '')}` : 'PDF, DOC up to 10MB'}
              </span>
            </>
          )}
        </label>
        {renderTemplateDownload()}
      </div>
    );

    if (isMultipleDoc) {
      return (
        <Disclosure as="div" className="mb-4">
          {({ open }) => (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <Disclosure.Button className="flex w-full items-center justify-between bg-gray-50 px-6 py-4">
                <div className="flex items-center gap-x-4">
                  <div className="h-12 w-12 flex-none rounded-lg bg-white ring-1 ring-gray-900/10 flex items-center justify-center">
                    <DocumentIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{label}</div>
                    <div className="mt-1">
                      {renderUploadStatus()}
                    </div>
                  </div>
                </div>
                <ChevronUpIcon
                  className={`${
                    open ? 'rotate-180 transform' : ''
                  } h-5 w-5 text-gray-500`}
                />
              </Disclosure.Button>
              
              <Disclosure.Panel className="px-6 py-4">
                {renderDottedUploadBox()}
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      );
    }
    
    return (
      <Disclosure as="div" className="mb-6">
        {({ open }) => (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <Disclosure.Button className="flex w-full items-center justify-between bg-gray-50 px-6 py-4">
              <div className="flex items-center gap-x-4">
                <div className="h-12 w-12 flex-none rounded-lg bg-white ring-1 ring-gray-900/10 flex items-center justify-center">
                  <DocumentIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">{doc.Name}</div>
                  <div className="mt-1">
                    {renderUploadStatus()}
                  </div>
                </div>
              </div>
              <ChevronUpIcon
                className={`${
                  open ? 'rotate-180 transform' : ''
                } h-5 w-5 text-gray-500`}
              />
            </Disclosure.Button>
            
            <Disclosure.Panel className="px-6 py-4">
              {renderDottedUploadBox()}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  };
  const handleEditAnswers = () => {
    // Set number of owners based on existing guarantors length
    setNumberOfOwners(personalGuarantors.length.toString());
    setShowDocumentUpload(false);
    setCurrentTab('Questions');
    // Optionally keep user on last step or reset to first step
    // setStep(0); // Remove or comment this line to stay on last step
  };
  const renderApplicationSummary = () => {
    const primeRate = 6.75;
    const rateSpread = 2.75;
    const totalRate = primeRate + rateSpread;
    const questionsNotSubmitted = !showDocumentUpload;

    // --- Determine Display Amount (unchanged) ---
    let displayAmount = parseFloat(loanAmount) || 0;
    if (termSheetAmount) {
      displayAmount = termSheetAmount;
    } else if (prequalifyAmount) {
      displayAmount = prequalifyAmount;
    }

    // --- Determine Display Label based on Status (unchanged) ---
    let displayLabel = "Requested Loan Amount";
    if (loanStatus === LoanStatuses.FUNDED || loanStatus === LoanStatuses.CLOSING || loanStatus === LoanStatuses.UNDERWRITING) {
        if (termSheetAmount) {
            displayLabel = "Approved Amount (Term Sheet)";
        } else if (prequalifyAmount) {
            displayLabel = "Approved Amount (Prequalified)";
        } else {
             displayLabel = "Approved Amount";
        }
    } else if (loanStatus === LoanStatuses.TERM_SHEET_EXTENDED) {
        displayLabel = "Term Sheet Amount";
    } else if (loanStatus === LoanStatuses.PREQUALIFIED_FOR) {
        displayLabel = "Prequalified Amount";
    }

    // --- Format Funding Date (unchanged) ---
    let formattedFundedDate = null;
    if (loanStatus === LoanStatuses.FUNDED && loanDataForNotifications?.fundedAt) {
        try {
            const date = loanDataForNotifications.fundedAt.toDate ? loanDataForNotifications.fundedAt.toDate() : new Date(loanDataForNotifications.fundedAt);
            formattedFundedDate = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
        } catch (e) {
            console.error("Error formatting funded date:", e);
            formattedFundedDate = "N/A";
        }
    }

    return (
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {businessName || 'New Application'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Application Overview</p>
            </div>
            <img
              src={profileImage}
              alt="Company Logo"
              className="h-8 w-auto object-contain"
            />
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <dt className="text-sm text-gray-500">Loan Type</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900">SBA 7(a) Term Loan</dd>
            </div>

            <div>
              <dt className="text-sm text-gray-500">{displayLabel}</dt>
              <dd className="mt-1 text-lg font-semibold text-blue-600">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0
                }).format(displayAmount)}
              </dd>
            </div>

            {/* Conditionally show Funding Date */}
            {loanStatus === LoanStatuses.FUNDED && formattedFundedDate && (
                 <div>
                   <dt className="text-sm text-gray-500">Date Funded</dt>
                   <dd className="mt-1 text-sm font-medium text-teal-600">{formattedFundedDate}</dd>
                 </div>
            )}

            {/* Show other details as long as questions ARE submitted */}
            {!questionsNotSubmitted && (
              <>
                <div>
                  <dt className="text-sm text-gray-500">Time to Receive</dt>
                  <dd className="mt-1 text-sm font-medium text-green-600">
                     {loanStatus === LoanStatuses.FUNDED ? 'Funded' : '2-3 Weeks (Est.)'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm text-gray-500">Est. Monthly Payment</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(displayAmount * 0.015 || 0)}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm text-gray-500">Repayment Term</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">10 years</dd>
                </div>

                <div>
                  <dt className="text-sm text-gray-500">Rate</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">
                    Prime + {rateSpread}% ({totalRate}%) (Est.)
                  </dd>
                </div>
              </>
            )}
          </div>

          {/* Show disclaimer as long as questions ARE submitted */}
          {!questionsNotSubmitted && (
            <div className="mt-8 text-xs text-gray-500 leading-5">
              Interest rates, loan term and monthly payments will be determined based on an analysis and review of information submitted to lenders and other available information. All loan information is presented without warranty, and the estimated terms are not binding in any way.
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const renderDocumentStepIndicator = () => {
      // Add console log to see what status is being used for rendering
      console.log("Rendering step indicator with loanStatus:", loanStatus);

      const pendingRequestedDocs = requestedDocsList.filter(doc => doc.status !== 'uploaded');
      const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
      let statusMessage = null;
      let statusIcon = <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />;
      let iconBg = 'bg-green-100';

      // --- Check for FUNDED status ---
      if (loanStatus === LoanStatuses.FUNDED) {
          console.log("Loan status is FUNDED.");
          statusMessage = {
              title: 'Congratulations! Your Loan is Funded!',
              subtitle: `The funds have been disbursed. We wish you the best!`
          };
          statusIcon = <CheckBadgeIcon className="h-6 w-6 text-teal-600" aria-hidden="true" />; // Use a funded icon
          iconBg = 'bg-teal-100'; // Use a distinct color for funded
      }
      // --- Check for CLOSING status ---
      else if (loanStatus === LoanStatuses.CLOSING) {
          console.log("Loan status is CLOSING. Pending closing docs:", pendingRequestedDocs.length);
          if (pendingRequestedDocs.length > 0) {
              statusMessage = {
                  title: 'In Closing: Please Upload Final Documents',
                  subtitle: `We need ${pendingRequestedDocs.length} more document(s) to finalize funding.`
              };
              statusIcon = <ArchiveBoxIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />;
              iconBg = 'bg-blue-100';
          } else {
              statusMessage = {
                  title: 'Closing Documents Received',
                  subtitle: 'Thank you! We will review the documents for final funding.'
              };
               statusIcon = <ArchiveBoxIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />;
              iconBg = 'bg-blue-100';
          }
      }
      // --- Check for DOCUMENT_CLARIFICATION status ---
      else if (loanStatus === LoanStatuses.DOCUMENT_CLARIFICATION) {
          console.log("Loan status is DOCUMENT_CLARIFICATION. Pending docs:", pendingRequestedDocs.length);
          if (pendingRequestedDocs.length > 0) {
              statusMessage = {
                  title: 'Action Required: Please Upload Requested Documents',
                  subtitle: `We need ${pendingRequestedDocs.length} more document(s) to proceed.`
              };
              statusIcon = <ExclamationCircleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />;
              iconBg = 'bg-yellow-100';
          } else {
              statusMessage = {
                  title: 'Requested Documents Received',
                  subtitle: 'Thank you! We will review the documents shortly.'
              };
              // Keep default check icon / bg for received state
          }
      }
      // --- Other statuses (Underwriting, Prequalified, Submitted etc.) ---
       else if (loanStatus === LoanStatuses.SUBMITTED) {
           console.log("Loan status is SUBMITTED.");
           statusMessage = { title: 'Application Submitted', subtitle: 'We are reviewing your application.' };
           iconBg = 'bg-green-100'; // Use green check for submitted
           statusIcon = <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />;
       }
      else if (loanStatus === LoanStatuses.UNDERWRITING) {
         console.log("Loan status is UNDERWRITING.");
         statusMessage = { title: 'Application in Underwriting', subtitle: 'Reviewing details...' }; // Simplified example
         iconBg = 'bg-indigo-100';
         statusIcon = <ClockIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />;
      }
       else if (loanStatus === LoanStatuses.PREQUALIFIED_FOR) {
          console.log("Loan status is PREQUALIFIED_FOR.");
          statusMessage = { title: 'Prequalified!', subtitle: 'Your application has been prequalified.' };
          iconBg = 'bg-blue-100';
          statusIcon = <CheckBadgeIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />;
       }
       else if (loanStatus === LoanStatuses.TERM_SHEET_EXTENDED) {
          console.log("Loan status is TERM_SHEET_EXTENDED.");
          statusMessage = { title: 'Term Sheet Extended', subtitle: 'Please review your term sheet.' };
          iconBg = 'bg-purple-100'; // Example color
          statusIcon = <DocumentIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />;
       }
      // Add checks for other relevant statuses if needed
      else {
           console.log("Loan status is:", loanStatus, "(No specific step indicator message)");
      }


      // --- Render the status message if one was determined ---
      if (statusMessage) {
         return (
            <nav aria-label="Progress" className="mb-8">
                <div className="text-center py-6">
                  <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${iconBg}`}>
                      {statusIcon}
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                      {statusMessage.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                      {statusMessage.subtitle}
                  </p>
                </div>
            </nav>
         );
      }

      // --- Fallback if no other condition met (e.g., initial state) ---
      console.log("No specific status message generated, rendering default or null.");
      return null; // Or render your original step-by-step visual if status is draft/new

  };

  const renderRequestedDocumentUploads = () => {
    // Filter based on the current loan status to show relevant pending requests
    let pendingRequests = [];
    let sectionTitle = '';
    let sectionBg = 'bg-yellow-50'; // Default for regular requests
    let borderColor = 'border-yellow-200';
    let titleColor = 'text-yellow-800';
    let uploadBorderColor = 'border-yellow-300 hover:border-yellow-400';
    let iconColor = 'text-yellow-500';

    if (loanStatus === LoanStatuses.CLOSING) {
        pendingRequests = requestedDocsList.filter(reqDoc => reqDoc.status !== 'uploaded');
        sectionTitle = 'Closing Documents Required';
        sectionBg = 'bg-blue-50';
        borderColor = 'border-blue-200';
        titleColor = 'text-blue-800';
        uploadBorderColor = 'border-blue-300 hover:border-blue-400';
        iconColor = 'text-blue-500';
    } else if (loanStatus === LoanStatuses.DOCUMENT_CLARIFICATION) {
        pendingRequests = requestedDocsList.filter(reqDoc => reqDoc.status !== 'uploaded');
        sectionTitle = 'Requested Documents';
        // Uses default yellow colors
    }

    if (pendingRequests.length === 0) return null;

    return (
      <div className="mt-8 pt-8 border-t border-dashed border-gray-300">
        <h3 className={`text-lg font-medium ${titleColor} mb-4`}>{sectionTitle}</h3>
        <div className="space-y-6">
          {pendingRequests.map((reqDoc) => {
            const documentKey = reqDoc.name; // The unique identifier is the requested name
            const isUploaded = uploadedFiles[documentKey]; // Check if a file exists with this name as the key

            return (
              <div key={documentKey} className={`${sectionBg} p-4 rounded-lg border ${borderColor}`}>
                 <h4 className={`text-md font-medium ${titleColor} mb-2`}>{reqDoc.name}</h4>
                 <div
                    className={classNames(
                      'border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center w-full',
                      isUploaded ? 'border-green-300 bg-green-50' : `${uploadBorderColor}`
                    )}
                 >
                    <input
                      type="file"
                      id={`requested-${documentKey.replace(/\s+/g, '-')}`}
                      // Pass null for standard args, only requestedDocName
                      onChange={(e) => handleFileUpload(e, null, null, null, reqDoc.name)}
                      className="sr-only"
                    />
                    <label
                      htmlFor={`requested-${documentKey.replace(/\s+/g, '-')}`}
                      className="cursor-pointer text-center w-full"
                    >
                      {isUploaded ? (
                        <>
                          <DocumentIcon className="mx-auto h-10 w-10 text-green-500" />
                          <span className="mt-2 block text-sm font-medium text-green-600">
                            {uploadedFiles[documentKey]?.name || 'File Uploaded'} {/* Safer access */}
                          </span>
                          <span className="mt-1 block text-xs text-green-500">
                            Click to replace
                          </span>
                        </>
                      ) : (
                        <>
                          <DocumentIcon className={`mx-auto h-10 w-10 ${iconColor}`} />
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload: {reqDoc.name}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                             Drop file here or click to browse
                          </span>
                        </>
                      )}
                    </label>
                 </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Function to get required documents based on loan amount
  const getRequiredDocuments = () => {
    const loanAmountNum = parseFloat(loanAmount) || 0;
    
    if (loanAmountNum < 350000) {
      // For loans under $350k, only require specific documents
      return requiredDocuments.filter(doc => {
        const docName = doc.Name;
        return (
          docName === "Business Tax Returns" ||
          docName === "Bank Statements" ||
          docName === "Debt Schedule" ||
          docName === "Personal Tax Returns"
        );
      }).map(doc => {
        // Modify the documents to only require current and previous year for tax returns
        if (doc.Name === "Business Tax Returns") {
          return {
            ...doc,
            RequiredDocuments: 2,
            Labels: [
              "Business Tax Return - Current Year",
              "Business Tax Return - Last Year"
            ]
          };
        }
        if (doc.Name === "Personal Tax Returns") {
          return {
            ...doc,
            RequiredDocuments: 2,
            Labels: [
              "Personal Tax Return - Current Year",
              "Personal Tax Return - Last Year"
            ]
          };
        }
        return doc;
      });
    }
    
    // For loans $350k and above, return all documents
    return requiredDocuments;
  };

  const renderDocumentUpload = () => {
    // --- Keep the original rendering logic ---
    const getDocumentDescription = (docName) => {
        // ... (keep existing description logic) ...
        const descriptions = {
          'Business Tax Returns': 'Upload the last 3 years of business tax returns.',
          'Personal Financial Statement': 'Upload Personal Financial Statement (SBA Form 413) for each guarantor. This form helps lenders understand your net worth and financial capacity.',
          'Bank Statements': 'Upload the last 3 months of business bank statements.',
          'Interim P&L': 'Upload your most recent interim profit and loss statement.',
          'Personal Tax Returns': 'Upload the last 3 years of personal tax returns for each guarantor.',
          'Debt Schedule': 'Upload a detailed schedule of all business debt.',
          'Balance Sheet': 'Upload your 2024 balance sheet showing your current assets and liabilities.',
          'AR/AP Aging Summary (If Applicable)': "Upload your accounts receivable and accounts payable aging summary. Only required if your business has accounts receivable or accounts payable.", // Add description for optional
          'Purchase Agreement (If Applicable)': "Upload draft of purchase agreement. Only required if you are purchasing real estate or an existing business.", // Add description for optional
        };
        return descriptions[docName] || '';
      };

      // --- Get filtered documents based on loan amount ---
      const filteredRequiredDocuments = getRequiredDocuments();

      // --- Define Optional Document Names ---
      const optionalDocNames = [
          "AR/AP Aging Summary (If Applicable)",
          "Purchase Agreement (If Applicable)"
      ];

      // --- Filter out optional documents for progress calculation ---
      const trulyRequiredDocuments = filteredRequiredDocuments.filter(
          doc => !optionalDocNames.includes(doc.Name)
      );

      // Helper function to check if a document section is complete (uses 'loan' state)
      const isDocumentSectionComplete = (doc) => {
          const cleanedDocName = doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim();
          // Check against the nested uploadedDocuments structure in the loan state
          return !!loan?.uploadedDocuments?.[cleanedDocName];
      };

      // Calculate completion status based on *truly required* documents
      const totalRequiredDocs = trulyRequiredDocuments.length;
      const completedRequiredDocs = trulyRequiredDocuments.filter(isDocumentSectionComplete).length;
      const progressPercent = totalRequiredDocs > 0 ? (completedRequiredDocs / totalRequiredDocs) * 100 : 0;
      const allStandardDocsUploaded = completedRequiredDocs === totalRequiredDocs;

      return (
        <div className="space-y-8">
          {/* --- ADD Conditionally display Submission Confirmation Message --- */}
          {loanStatus === LoanStatuses.SUBMITTED && (
              <div className="text-center py-6 px-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <h3 className="mt-3 text-lg font-medium text-gray-900">Application Submitted</h3>
                  <p className="mt-1 text-sm text-gray-500">
                      Thank you! We have received your initial required documents. We will review them and be in touch if anything else is needed. You can still upload optional documents below if applicable.
                  </p>
              </div>
          )}
          {/* --- END Submission Confirmation Message --- */}

          {/* --- MOVE STEP INDICATOR HERE --- */}
          {/* Render Step Indicator (shows status like Submitted, Clarification, Closing etc.) */}
          {renderDocumentStepIndicator()}
          {/* --- END MOVE --- */}

          {/* Document Progress Indicator (Show unless FUNDED or maybe other final states) */}
          {loanStatus !== LoanStatuses.FUNDED && loanStatus !== LoanStatuses.CLOSING && loanStatus !== LoanStatuses.DOCUMENT_CLARIFICATION && ( // Hide progress if in clarification/closing stages where step indicator takes over
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 mb-2">
                      Document Status ({completedRequiredDocs}/{totalRequiredDocs} required sections complete)
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                      {allStandardDocsUploaded
                          ? "All standard required documents uploaded. Additional documents may be requested or optionally provided below."
                          : `Please upload the remaining ${totalRequiredDocs - completedRequiredDocs} required file(s) to complete your application.`
                      }
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                      <div
                          className="bg-indigo-600 h-2.5 rounded-full"
                          style={{ width: `${progressPercent}%` }}
                      ></div>
                  </div>
                  <ul className="space-y-1 text-sm">
                      {trulyRequiredDocuments.map(doc => {
                          const isComplete = isDocumentSectionComplete(doc);
                          return (
                              <li key={doc.DocumentID} className="flex items-center">
                                  {isComplete ? (
                                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                                  ) : (
                                      <ExclamationCircleIcon className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                                  )}
                                  <span className={isComplete ? "text-gray-600" : "text-gray-800 font-medium"}>
                                      {doc.Name}
                                  </span>
                              </li>
                          );
                      })}
                  </ul>
              </div>
          )}

          {/* Render Requested Documents Section (if any) */}
          {renderRequestedDocumentUploads()}

          {/* Render ALL document upload sections (including optional) */}
          {filteredRequiredDocuments.map((doc) => {
             const isOptional = optionalDocNames.includes(doc.Name);
             const isComplete = isDocumentSectionComplete(doc); // Check completion status

             // Determine the rendering logic based on whether the document is individual
             if (doc.Individual) {
                 // Render a section for each guarantor
                 return personalGuarantors.map((guarantor, guarantorIndex) => (
                     <div key={`${doc.DocumentID}-${guarantorIndex}`} className={`bg-white pt-6 border-t border-gray-200 ${isComplete && loanStatus === LoanStatuses.SUBMITTED ? 'opacity-70' : ''}`}>
                         <div className="mb-4 flex justify-between items-start">
                             <div>
                                 <h3 className="text-lg font-medium text-gray-900">
                                     {doc.Name} - {guarantor.firstName} {guarantor.lastName}
                                     {isOptional && <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>}
                                     {/* Individual completion check might be more complex - this checks the base doc */}
                                     {isComplete && loanStatus !== LoanStatuses.NEW_APPLICATION && <CheckCircleIcon className="ml-2 inline-block h-4 w-4 text-green-500" />}
                                 </h3>
                                 <p className="mt-1 text-sm text-gray-500">
                                     {getDocumentDescription(doc.Name)}
                                 </p>
                                 {doc.SubDescription && (
                                     <p className="mt-1 text-sm text-gray-500 italic">
                                         {doc.SubDescription}
                                     </p>
                                 )}
                             </div>
                             {doc.TemplateLink && (
                                 <a
                                     href={doc.TemplateLink}
                                     download
                                     className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 flex-shrink-0 ml-4"
                                 >
                                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                     </svg>
                                     Download Template
                                 </a>
                             )}
                         </div>

                         {/* Render cards for each label *for this specific guarantor* */}
                         {doc.Labels.length > 1 ? (
                             <div className="space-y-4">
                                 {doc.Labels.map((label) => {
                                     const cleanDocName = doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim();
                                     // Generate key specific to this guarantor
                                     const documentKey = `${cleanDocName}-${label}-${guarantor.firstName} ${guarantor.lastName}`;
                                     return renderDocumentCard(doc, label, documentKey, guarantor); // Pass the specific guarantor
                                 })}
                             </div>
                         ) : (
                             // Generate key specific to this guarantor
                             renderDocumentCard(doc, doc.Labels[0], `${doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim()}-${doc.Labels[0]}-${guarantor.firstName} ${guarantor.lastName}`, guarantor) // Pass the specific guarantor
                         )}
                     </div>
                 ));
             } else {
                 // Render non-individual document section once
                 return (
                     <div key={doc.DocumentID} className={`bg-white pt-6 border-t border-gray-200 ${isComplete && loanStatus === LoanStatuses.SUBMITTED ? 'opacity-70' : ''}`}>
                         <div className="mb-4 flex justify-between items-start">
                             <div>
                                 <h3 className="text-lg font-medium text-gray-900">
                                     {doc.Name}
                                     {isOptional && <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>}
                                     {isComplete && loanStatus !== LoanStatuses.NEW_APPLICATION && <CheckCircleIcon className="ml-2 inline-block h-4 w-4 text-green-500" />}
                                 </h3>
                                 <p className="mt-1 text-sm text-gray-500">
                                     {getDocumentDescription(doc.Name)}
                                 </p>
                                 {doc.SubDescription && (
                                     <p className="mt-1 text-sm text-gray-500 italic">
                                         {doc.SubDescription}
                                     </p>
                                 )}
                             </div>
                             {doc.TemplateLink && (
                                 <a
                                     href={doc.TemplateLink}
                                     download
                                     className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 flex-shrink-0 ml-4"
                                 >
                                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                     </svg>
                                     Download Template
                                 </a>
                             )}
                         </div>

                         {/* Render cards for each label, passing null for guarantor */}
                         {doc.Labels.length > 1 ? (
                             <div className="space-y-4">
                                 {doc.Labels.map((label) => {
                                     const cleanDocName = doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim();
                                     const documentKey = `${cleanDocName}-${label}`;
                                     return renderDocumentCard(doc, label, documentKey, null); // Pass null for guarantor
                                 })}
                             </div>
                         ) : (
                              // Pass null for guarantor
                             renderDocumentCard(doc, doc.Labels[0], `${doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim()}-${doc.Labels[0]}`, null) // Pass null for guarantor
                         )}
                     </div>
                 );
             }
           })}
        </div>
      );
    };

  // Update the standalone 'allDocumentsUploaded' check if it's used elsewhere (e.g., Tabs)
  // This check might need refinement based on its exact purpose now.
  // For the Tabs checkmark, using the same logic as above might be best:
  const checkAllStandardDocsUploadedForTabs = () => {
      const optionalDocNames = [
          "AR/AP Aging Summary (If Applicable)",
          "Purchase Agreement (If Applicable)"
      ];
      const filteredRequiredDocuments = getRequiredDocuments();
      const trulyRequiredDocuments = filteredRequiredDocuments.filter(
          doc => !optionalDocNames.includes(doc.Name)
      );
      const isComplete = (doc) => {
           const cleanedDocName = doc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim();
           return !!loan?.uploadedDocuments?.[cleanedDocName];
      };
      return trulyRequiredDocuments.length > 0 && trulyRequiredDocuments.every(isComplete);
  };

  const allDocumentsUploaded = requiredDocuments.every(doc => 
    doc.Labels.every(label => 
      doc.Individual 
        ? personalGuarantors.every(guarantor => 
            uploadedFiles[`${doc.Name.replace(/[()]/g, '').trim()}-${label}-${guarantor.firstName} ${guarantor.lastName}`]
          )
        : uploadedFiles[`${doc.Name.replace(/[()]/g, '').trim()}-${label}`]
    )
  );

  const person = {
    name: 'Isak',
    role: 'Application Manager',
    title: 'CEO, Lended',
    email: 'isak@lended.ai',
    telephone: '+1 (914) 414-1106',
    imageUrl: profileImage,
  };

  const renderTabs = () => {
    // Use the refined check for the checkmark logic
    const allStandardDocsCompleted = checkAllStandardDocsUploadedForTabs();

    return (
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setCurrentTab('Questions')}
            className={classNames(
              currentTab === 'Questions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center'
            )}
          >
            Questions
            {showDocumentUpload && (
              <CheckIcon className="ml-2 h-4 w-4 text-green-500" aria-hidden="true" />
            )}
          </button>
          <button
            onClick={() => showDocumentUpload && setCurrentTab('Documents')}
            className={classNames(
              currentTab === 'Documents'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500',
              'whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center',
              !showDocumentUpload && 'opacity-50 cursor-not-allowed'
            )}
          >
            Documents
            {/* Use the new check here */}
            {allStandardDocsCompleted && (
              <CheckIcon className="ml-2 h-4 w-4 text-green-500" aria-hidden="true" />
            )}
          </button>
        </nav>
      </div>
    );
  };

  const renderStepIndicator = () => (
    <nav aria-label="Progress" className="mb-8">
      <ol role="list" className="space-y-4 md:flex md:space-x-8 md:space-y-0">
        {[
          'Ownership Information',
          'Personal Guarantors',
          'Business Information',
          'Loan Details',
          'Employee Count'
        ].map((stepName, index) => (
          <li key={stepName} className="md:flex-1">
            <div className={classNames(
              'flex flex-col border-l-4 py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4',
              step === index 
                ? 'border-indigo-600' 
                : step > index 
                  ? 'border-green-500'
                  : 'border-gray-200'
            )}>
              <span className={classNames(
                'text-sm font-medium',
                step === index 
                  ? 'text-indigo-600' 
                  : step > index 
                    ? 'text-green-500'
                    : 'text-gray-500'
              )}>
                Step {index + 1}
              </span>
              <span className="text-sm">{stepName}</span>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );

  const renderMainContent = () => {
    return (
      <div className="min-h-full">
        <div className="bg-indigo-600 pb-32">
          <header className="py-10">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <h1 className="text-3xl font-bold tracking-tight text-white">
                Loan Application
              </h1>
            </div>
          </header>
        </div>

        <main className="-mt-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
              {/* Left column */}
              <div className="grid grid-cols-1 gap-4 lg:col-span-2">
                <section aria-labelledby="section-1-title">
                  <div className="overflow-hidden rounded-lg bg-white shadow">
                    <div className="p-6">
                      {renderTabs()}

                      {currentTab === 'Questions' ? (
                        renderQuestionsContent()
                      ) : (
                        renderDocumentUpload()
                      )}
                    </div>
                  </div>
                </section>
              </div>

              {/* Right column */}
              <div className="grid grid-cols-1 gap-4">
                <section aria-labelledby="section-2-title">
                  {renderApplicationSummary()}
                  {renderContactCard()}
                </section>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  };

  const renderQuestionsContent = () => {
    if (showDocumentUpload) {
      return (
        <div className="text-center py-12">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg 
              className="h-6 w-6 text-green-600" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Questions Completed</h3>
          <p className="mt-2 text-sm text-gray-500">
            You have completed all the required questions. You can now proceed to upload your documents.
          </p>
          <div className="mt-6 flex justify-center space-x-4">
            <button
              onClick={() => setCurrentTab('Documents')}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Go to Document Upload
              <svg 
                className="ml-2 -mr-0.5 h-4 w-4" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={2} 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M8.25 4.5l7.5 7.5-7.5 7.5" 
                />
              </svg>
            </button>
            <button
              onClick={handleEditAnswers}
              className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Edit Answers
              <svg
                className="ml-2 -mr-0.5 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between">
            {[
              'Ownership Information',
              'Personal Guarantors',
              'Business Information',
              'Loan Details',
              'Employee Count'
            ].map((stepName, index) => (
              <div key={stepName} className="flex flex-col items-center">
                <div className={`h-2 w-full ${index <= step ? 'bg-green-500' : 'bg-gray-200'}`} />
                <div className="mt-2">
                  <span className={`text-sm ${step === index ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                    Step {index + 1}
                  </span>
                  <p className="text-sm text-gray-500">{stepName}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Questions Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {renderForm()}
          <div className="flex justify-between pt-6">
            {step > 0 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Previous
              </button>
            )}
            {step < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Continue to Document Upload
              </button>
            )}
          </div>
        </form>
      </>
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e, docName, label) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length) {
      // Use the existing handleFileUpload function with the dropped file
      const event = { target: { files: [files[0]] } };
      await handleFileUpload(event, docName, label);
    }
  };

  const renderContactCard = () => {
    const person = {
      name: 'Isak',
      title: 'CEO, Lended',
      role: 'Application Manager',
      email: 'isak@lended.ai',
      telephone: '+1 (914) 414-1106',
      imageUrl: profileImage,
    };

    return (
      <div className="mt-4 col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow">
        <div className="flex w-full items-center justify-between space-x-6 p-6">
          <div className="flex-1 truncate">
            <div className="flex items-center space-x-3">
              <h3 className="truncate text-sm font-medium text-gray-900">{person.name}</h3>
              <span className="inline-flex flex-shrink-0 items-center rounded-full bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                {person.role}
              </span>
            </div>
            <p className="mt-1 truncate text-sm text-gray-500">{person.title}</p>
          </div>
          <img 
            className="h-10 w-auto flex-shrink-0 object-contain" 
            src={person.imageUrl} 
            alt="" 
          />
        </div>
        <div>
          <div className="-mt-px flex divide-x divide-gray-200">
            <div className="flex w-0 flex-1">
              <a
                href={`mailto:${person.email}`}
                className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
              >
                <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                Email
              </a>
            </div>
            <div className="-ml-px flex w-0 flex-1">
              <a
                href={`tel:${person.telephone}`}
                className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
              >
                <PhoneIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                Call
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSuccessNotification = () => {
    if (!showSuccessNotification) return null;

    return (
      <div className="fixed bottom-4 right-4 bg-green-50 p-4 rounded-md shadow-lg border border-green-100">
        <div className="flex items-center">
          <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
          <p className="ml-3 text-sm font-medium text-green-800">{successMessage}</p>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {renderSuccessNotification()}
      <div className="bg-indigo-600 pb-32">
        <header className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              Loan Application
            </h1>
          </div>
        </header>
      </div>

      <main className="-mt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
            {/* Left column */}
            <div className="grid grid-cols-1 gap-4 lg:col-span-2">
              <section aria-labelledby="section-1-title">
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-6">
                    {renderTabs()}

                    {currentTab === 'Questions' ? (
                      renderQuestionsContent()
                    ) : (
                      renderDocumentUpload()
                    )}
                  </div>
                </div>
              </section>
            </div>

            {/* Right column */}
            <div className="grid grid-cols-1 gap-4">
              <section aria-labelledby="section-2-title">
                {renderApplicationSummary()}
                {renderContactCard()}
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SharedUploadPage;
